import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { submitEnquiry } from '../api/api';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { CheckCircle } from 'lucide-react';

const EnquiryPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    company_name: '',
    email: '',
    phone: '',
    preferred_location: '',
    number_of_guests: '',
    start_date: '',
    length_of_stay: '',
    additional_info: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        number_of_guests: parseInt(formData.number_of_guests) || 1
      };

      await submitEnquiry(submitData);
      
      toast.success('Enquiry Submitted Successfully!', {
        description: "We'll review your enquiry and get back to you promptly with availability and options.",
      });

      // Reset form
      setFormData({
        full_name: '',
        company_name: '',
        email: '',
        phone: '',
        preferred_location: '',
        number_of_guests: '',
        start_date: '',
        length_of_stay: '',
        additional_info: ''
      });

      // Navigate to home after a delay
      setTimeout(() => {
        navigate('/');
      }, 2000);

    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast.error('Submission Failed', {
        description: 'There was an error submitting your enquiry. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#f8f9fa' }}>
      <Navbar />
      <Toaster position="top-center" richColors />

      {/* Header */}
      <section className="text-white py-20" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f4c75 100%)' }}>
        <div className="container mx-auto px-4 text-center">
          <div className="inline-block mb-4">
            <span className="badge-premium">Contact Us</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6" data-testid="enquiry-page-title" style={{ letterSpacing: '-0.02em' }}>
            Make an Enquiry
          </h1>
          <p className="text-xl max-w-3xl mx-auto" style={{ color: '#e8e8e8' }}>
            Tell us a little about your requirements and we'll get back to you promptly with availability and options.
          </p>
        </div>
      </section>

      {/* Enquiry Form */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <Card className="rounded-2xl shadow-2xl border-2" style={{ borderColor: '#e8e8e8' }}>
            <CardContent className="p-10">
              <div className="mb-8 text-center">
                <h2 className="text-3xl font-bold mb-3" style={{ color: '#1a1a2e' }}>Get in Touch</h2>
                <p style={{ color: '#636e72' }}>Fill in the form below and our team will respond within 24 hours</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="full_name" className="text-base font-semibold mb-2" style={{ color: '#2d3436' }}>Full Name *</Label>
                    <Input
                      id="full_name"
                      name="full_name"
                      data-testid="full-name-input"
                      value={formData.full_name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                      className="mt-2 h-12 rounded-xl border-2 focus:border-[#d4a574] transition"
                    />
                  </div>

                  <div>
                    <Label htmlFor="company_name" className="text-base font-semibold mb-2" style={{ color: '#2d3436' }}>Company Name *</Label>
                    <Input
                      id="company_name"
                      name="company_name"
                      data-testid="company-name-input"
                      value={formData.company_name}
                      onChange={handleChange}
                      required
                      placeholder="Your Company Ltd"
                      className="mt-2 h-12 rounded-xl border-2 focus:border-[#d4a574] transition"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email" className="text-base font-semibold mb-2" style={{ color: '#2d3436' }}>Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="email-input"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@company.com"
                      className="mt-2 h-12 rounded-xl border-2 focus:border-[#d4a574] transition"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-base font-semibold mb-2" style={{ color: '#2d3436' }}>Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      data-testid="phone-input"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+44 7700 900000"
                      className="mt-2 h-12 rounded-xl border-2 focus:border-[#d4a574] transition"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="preferred_location" className="text-base font-semibold mb-2" style={{ color: '#2d3436' }}>Preferred Location *</Label>
                  <Input
                    id="preferred_location"
                    name="preferred_location"
                    data-testid="location-input"
                    value={formData.preferred_location}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Chester, Wirral, Ellesmere Port"
                    className="mt-2 h-12 rounded-xl border-2 focus:border-[#d4a574] transition"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label htmlFor="number_of_guests" className="text-base font-semibold mb-2" style={{ color: '#2d3436' }}>Number of Guests *</Label>
                    <Input
                      id="number_of_guests"
                      name="number_of_guests"
                      type="number"
                      min="1"
                      data-testid="guests-input"
                      value={formData.number_of_guests}
                      onChange={handleChange}
                      required
                      placeholder="2"
                      className="mt-2 h-12 rounded-xl border-2 focus:border-[#d4a574] transition"
                    />
                  </div>

                  <div>
                    <Label htmlFor="start_date" className="text-base font-semibold mb-2" style={{ color: '#2d3436' }}>Start Date *</Label>
                    <Input
                      id="start_date"
                      name="start_date"
                      type="date"
                      data-testid="start-date-input"
                      value={formData.start_date}
                      onChange={handleChange}
                      required
                      className="mt-2 h-12 rounded-xl border-2 focus:border-[#d4a574] transition"
                    />
                  </div>

                  <div>
                    <Label htmlFor="length_of_stay" className="text-base font-semibold mb-2" style={{ color: '#2d3436' }}>Length of Stay *</Label>
                    <Input
                      id="length_of_stay"
                      name="length_of_stay"
                      data-testid="length-input"
                      value={formData.length_of_stay}
                      onChange={handleChange}
                      required
                      placeholder="e.g., 2 weeks, 3 months"
                      className="mt-2 h-12 rounded-xl border-2 focus:border-[#d4a574] transition"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="additional_info" className="text-base font-semibold mb-2" style={{ color: '#2d3436' }}>Additional Information</Label>
                  <Textarea
                    id="additional_info"
                    name="additional_info"
                    data-testid="additional-info-input"
                    value={formData.additional_info}
                    onChange={handleChange}
                    placeholder="Any specific requirements or questions?"
                    className="mt-2 rounded-xl border-2 focus:border-[#d4a574] transition"
                    rows={5}
                  />
                </div>

                <Button 
                  type="submit" 
                  data-testid="submit-enquiry-button"
                  disabled={loading}
                  className="w-full btn-primary-custom text-lg py-7 rounded-full font-semibold"
                >
                  {loading ? 'Submitting...' : 'Send Enquiry'}
                </Button>
              </form>

              <div className="mt-8 p-6 rounded-xl" style={{ background: '#f8f9fa' }}>
                <div className="flex items-start space-x-3">
                  <CheckCircle size={24} className="flex-shrink-0 mt-1" style={{ color: '#d4a574' }} />
                  <p className="text-sm italic" style={{ color: '#636e72' }}>
                    We'll review your enquiry and get back to you promptly with availability and options. Our team typically responds within 24 hours during business days.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EnquiryPage;